import { useAuth } from "@/hooks/useAuth";
import { deriveWorkspacePersona, type WorkspacePersona } from "@/config/workspacePersona";
import { readDevPersonaOverride, writeDevPersonaOverride } from "@/lib/devPersonaOverride";
import { cn } from "@/lib/utils";

/**
 * Vite dev server only: switch `workspace_persona` preview without editing Supabase.
 * Stored in localStorage; cleared on sign-out.
 */
export function DevPersonaSwitcher() {
  const { isAuthenticated, userData, bumpDevPersonaPreview } = useAuth();

  if (!import.meta.env.VITE_DEV_TOOLS || !isAuthenticated || !userData || !bumpDevPersonaPreview) {
    return null;
  }

  const override = readDevPersonaOverride();
  const effective = deriveWorkspacePersona(userData);

  const apply = (next: WorkspacePersona | null) => {
    writeDevPersonaOverride(next);
    bumpDevPersonaPreview();
  };

  const chip = (label: string, active: boolean, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-md px-2 py-1 text-[11px] font-medium transition-colors",
        active
          ? "bg-bodhi-teal text-white shadow-sm"
          : "bg-stone-100 text-stone-700 hover:bg-stone-200"
      )}
    >
      {label}
    </button>
  );

  return (
    <div
      className="fixed bottom-4 right-4 z-[100] max-w-[min(100vw-2rem,14rem)] rounded-lg border border-stone-200 bg-white/95 p-2 shadow-lg backdrop-blur-sm"
      role="region"
      aria-label="Development workspace persona preview"
    >
      <p className="mb-1.5 font-inter text-[10px] font-semibold uppercase tracking-wide text-stone-500">Dev persona</p>
      <p className="mb-2 font-inter text-[10px] leading-snug text-stone-600">
        Effective nav: <span className="font-medium text-stone-800">{effective}</span>
      </p>
      <div className="flex flex-wrap gap-1">
        {chip("Database", override === null, () => apply(null))}
        {chip("Leader", override === "leader", () => apply("leader"))}
        {chip("District", override === "district_leader", () => apply("district_leader"))}
        {chip("Teacher", override === "teacher", () => apply("teacher"))}
        {chip("Support", override === "support_staff", () => apply("support_staff"))}
        {chip("Classified", override === "classified_staff", () => apply("classified_staff"))}
      </div>
    </div>
  );
}
